/**
 * Écriture des fichiers reçus.
 *
 * ## La contrainte qui dicte la conception
 *
 * `showSaveFilePicker` exige une **activation utilisateur transitoire**. Un clic ne
 * l'accorde qu'une fois : impossible d'enchaîner un sélecteur par fichier. Ce module est
 * donc structuré pour n'appeler le sélecteur qu'**une seule fois**, sur le chemin critique
 * du clic d'acceptation :
 *
 * - un fichier → `showSaveFilePicker` (une autorisation, une écriture en flux) ;
 * - plusieurs fichiers → `showDirectoryPicker` (une autorisation, N fichiers créés dedans) ;
 * - sinon → accumulation en mémoire puis déclenchement du téléchargement par le navigateur,
 *   qui lui n'exige aucune autorisation.
 *
 * Le sélecteur est appelé **avant tout autre `await`** : dès qu'on attend autre chose,
 * l'activation est consommée et l'appel échoue.
 */

import type { Bytes, FileMeta } from '@attrape/shared';
import { LocalizedError, msg } from '../../i18n/index.js';
import type { WriteStrategy } from '../browser.js';
import { openStreamDownload, type StreamWriter } from './stream-download.js';

export interface FileSink {
  readonly name: string;
  /**
   * Poignée de fichier persistable, quand la stratégie écrit directement sur le disque.
   *
   * Exposée pour la reprise (Phase 5) : c'est elle qui permet de **rouvrir le même
   * fichier** après une coupure, au lieu d'en créer un second à chaque tentative. En
   * stratégie mémoire, il n'y a pas de poignée — et rien à reprendre non plus, puisque
   * le tampon vit dans l'onglet.
   */
  readonly handle?: FileSystemFileHandle | null;
  /**
   * Écrit un morceau.
   *
   * `position` (octet absolu dans le fichier) n’est honorée que par les sinks capables
   * de **seek** : la File System Access API le permet (`write({ type: 'write',
   * position, data })`). Les sinks en flux pur (téléchargement par Service Worker) ne
   * savent qu’ajouter au curseur : ils ignorent la position.
   *
   * C’est ce qui rend la reprise sûre : après une coupure, l’émetteur renvoie des
   * morceaux *manquants* à des offsets arbitraires. Sans seek, ces morceaux
   * s’écriraient bout à bout au curseur — et produiraient un fichier **complet mais
   * corrompu**, le pire résultat pour un outil de transfert.
   */
  write(chunk: Bytes, position?: number): Promise<void>;
  /** Finalise l'écriture et rend le fichier visible par l'utilisateur. */
  commit(): Promise<void>;
  /** Libère les ressources sans produire de fichier exploitable. */
  abort(): Promise<void>;
}

export interface SinkSet {
  readonly sinks: FileSink[];
  readonly strategy: WriteStrategy;
  abortAll(): Promise<void>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Implémentations
// ─────────────────────────────────────────────────────────────────────────────

/** Écriture en flux dans un fichier choisi par l'utilisateur. Aucune limite de taille. */
class StreamSink implements FileSink {
  constructor(
    readonly name: string,
    private readonly writable: FileSystemWritableFileStream,
    readonly handle: FileSystemFileHandle | null = null,
  ) {}

  async write(chunk: Bytes, position?: number): Promise<void> {
    // Seek explicite : sur une reprise, le morceau n'est pas contigu au curseur. Écrire
    // à la bonne position est ce qui reconstruit le fichier à l'identique ; un ajout
    // au curseur produirait un fichier complet mais corrompu.
    if (position !== undefined) {
      await this.writable.write({ type: 'write', position, data: chunk });
    } else {
      await this.writable.write(chunk);
    }
  }

  async commit(): Promise<void> {
    await this.writable.close();
  }

  async abort(): Promise<void> {
    try {
      await this.writable.abort();
    } catch {
      // Le flux peut déjà être fermé : abandonner ne doit jamais lever à son tour.
    }
  }
}

/**
 * Écriture en flux dans un téléchargement servi par le Service Worker.
 *
 * Le navigateur écrit le fichier sur disque au fur et à mesure : la mémoire de l'onglet
 * ne contient jamais le fichier entier. C'est la réponse au « Safari/Firefox plante sur
 * les gros fichiers », dans la limite de ce que les navigateurs permettent (voir
 * `docs/ADR-001` pour Safari, qui reste non couvert).
 */
class StreamDownloadSink implements FileSink {
  private writer: StreamWriter | null = null;
  private readonly ready: Promise<void>;

  constructor(readonly name: string) {
    this.ready = openStreamDownload(name).then((writer) => {
      this.writer = writer;
    });
  }

  async write(chunk: Bytes): Promise<void> {
    await this.ready;
    await this.writer?.write(chunk);
  }

  async commit(): Promise<void> {
    await this.ready;
    await this.writer?.close();
  }

  async abort(): Promise<void> {
    // `abort` peut être appelé avant que le flux soit ouvert : on attend le flux pour
    // pouvoir le clore proprement plutôt que de laisser un téléchargement suspendu.
    await this.ready.catch(() => undefined);
    await this.writer?.abort();
  }
}

/**
 * Accumulation en mémoire puis téléchargement.
 *
 * Utilisé faute de File System Access API. Le pic d'occupation est celui du plus gros
 * fichier du lot — les fichiers sont traités séquentiellement et le tampon est libéré
 * à chaque `commit`.
 */
class MemorySink implements FileSink {
  private parts: Bytes[] = [];
  private bytes = 0;

  constructor(
    readonly name: string,
    private readonly mime: string,
  ) {}

  async write(chunk: Bytes): Promise<void> {
    this.parts.push(chunk);
    this.bytes += chunk.byteLength;
  }

  async commit(): Promise<void> {
    const blob = new Blob(this.parts, { type: this.mime });
    this.parts = [];
    this.bytes = 0;
    triggerDownload(blob, this.name);
  }

  async abort(): Promise<void> {
    this.parts = [];
    this.bytes = 0;
  }

  get bufferedBytes(): number {
    return this.bytes;
  }
}

/**
 * Déclenche un téléchargement sans quitter la page.
 *
 * L'URL d'objet est révoquée avec un délai : la révoquer immédiatement après le clic
 * annule le téléchargement dans plusieurs navigateurs, qui n'ont pas encore commencé
 * à lire le blob à cet instant.
 */
function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.rel = 'noopener';
  anchor.style.display = 'none';
  document.body.append(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Retire ce qui pourrait être interprété comme un chemin ou un caractère de contrôle
 * par le système de fichiers.
 *
 * Filtrage par point de code plutôt que par expression régulière : l'intention est ici
 * explicite (« écarter les caractères de contrôle »), là où une classe de caractères
 * `\u0000-\u001f` se relit mal et ressemble à une erreur de saisie.
 */
export function sanitizeFileName(name: string): string {
  const cleaned = Array.from(name.replace(/[/\\]/g, '_'))
    .filter((character) => {
      const code = character.codePointAt(0) ?? 0;
      return code > 0x1f && code !== 0x7f;
    })
    .join('')
    .replace(/^\.+/, '')
    .trim();

  return cleaned.length > 0 ? cleaned.slice(0, 255) : 'fichier';
}

/** Ajoute un suffixe aux doublons pour éviter qu'un fichier en écrase un autre. */
function uniqueNames(files: FileMeta[]): string[] {
  const used = new Map<string, number>();
  return files.map((file) => {
    const base = sanitizeFileName(file.name);
    const seen = used.get(base) ?? 0;
    used.set(base, seen + 1);
    if (seen === 0) return base;

    const dot = base.lastIndexOf('.');
    if (dot <= 0) return `${base} (${seen})`;
    return `${base.slice(0, dot)} (${seen})${base.slice(dot)}`;
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Fabriques — appeler depuis un gestionnaire de clic
// ─────────────────────────────────────────────────────────────────────────────

/**
 * L'utilisateur a refusé de choisir une destination.
 *
 * Distinct d'une erreur : ce n'est pas un dysfonctionnement, c'est un choix. L'interface
 * doit revenir à l'état initial sans afficher de message d'échec.
 */
export class DestinationCancelledError extends Error {
  constructor() {
    super('Enregistrement annulé.');
    this.name = 'DestinationCancelledError';
  }
}

/**
 * Traduit une erreur du sélecteur en message exploitable.
 *
 * Les cas réels qui comptent : une politique d'entreprise peut exposer la fonction tout en
 * la bloquant, et l'activation utilisateur peut avoir expiré si un `await` s'est glissé
 * avant l'appel. Sans traduction, l'utilisateur verrait un `SecurityError` brut.
 */
function translatePickerError(error: unknown): Error {
  if (error instanceof DOMException) {
    if (error.name === 'AbortError') return new DestinationCancelledError();
    if (error.name === 'SecurityError') {
      return new LocalizedError(msg.key('errors.destinationUnavailable'));
    }
    if (error.name === 'NotAllowedError') {
      return new LocalizedError(msg.key('errors.destinationDenied'));
    }
  }
  return error instanceof Error ? error : new Error(String(error));
}

/**
 * Prépare les destinations des fichiers reçus.
 *
 * **À appeler directement depuis le gestionnaire de clic** : le premier `await` de cette
 * fonction doit être l'appel au sélecteur, sinon l'activation utilisateur est perdue.
 */
export async function createSinkSet(files: FileMeta[], strategy: WriteStrategy): Promise<SinkSet> {
  // Le premier appel asynchrone est le sélecteur : ne rien intercaler avant.
  if (strategy === 'fs-access' && files.length === 1) {
    return createSingleFileSink(files[0] as FileMeta);
  }
  if (strategy === 'directory' && files.length > 0) {
    return createDirectorySinks(files);
  }
  if (strategy === 'streamsaver' && files.length === 1) {
    return createStreamDownloadSink(files[0] as FileMeta);
  }
  return createMemorySinks(files);
}

async function createSingleFileSink(file: FileMeta): Promise<SinkSet> {
  const picker = window.showSaveFilePicker;
  if (typeof picker !== 'function') {
    throw new LocalizedError(msg.key('errors.noPicker'));
  }

  let handle: FileSystemFileHandle;
  try {
    handle = await picker.call(window, { suggestedName: sanitizeFileName(file.name) });
  } catch (error) {
    throw translatePickerError(error);
  }

  const writable = await handle.createWritable();
  const sink = new StreamSink(sanitizeFileName(file.name), writable, handle);

  return {
    sinks: [sink],
    strategy: 'fs-access',
    async abortAll() {
      await sink.abort();
    },
  };
}

async function createDirectorySinks(files: FileMeta[]): Promise<SinkSet> {
  const picker = window.showDirectoryPicker;
  if (typeof picker !== 'function') {
    throw new LocalizedError(msg.key('errors.noDirectoryPicker'));
  }

  let directory: FileSystemDirectoryHandle;
  try {
    directory = await picker.call(window, { mode: 'readwrite' });
  } catch (error) {
    throw translatePickerError(error);
  }

  const names = uniqueNames(files);
  const sinks: FileSink[] = [];

  // Une seule autorisation couvre la création de tous les fichiers du dossier.
  for (const [index, file] of files.entries()) {
    const name = names[index] ?? sanitizeFileName(file.name);
    const handle = await directory.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    sinks.push(new StreamSink(name, writable, handle));
  }

  return {
    sinks,
    strategy: 'directory',
    async abortAll() {
      await Promise.all(sinks.map((sink) => sink.abort()));
    },
  };
}

/**
 * Prépare un téléchargement en flux.
 *
 * Aucun sélecteur n'est requis : le navigateur écrit là où il a l'habitude d'écrire ses
 * téléchargements. La séquence d'ouverture du flux (enregistrement, acquittement,
 * transfert) est décrite dans `stream-download.ts` et ne doit pas être réordonnée.
 */
function createStreamDownloadSink(file: FileMeta): SinkSet {
  const name = sanitizeFileName(file.name);
  const sink = new StreamDownloadSink(name);

  return {
    sinks: [sink],
    strategy: 'streamsaver',
    async abortAll() {
      await sink.abort();
    },
  };
}

function createMemorySinks(files: FileMeta[]): SinkSet {
  const names = uniqueNames(files);
  const sinks = files.map(
    (file, index) => new MemorySink(names[index] ?? sanitizeFileName(file.name), file.mime),
  );

  return {
    sinks,
    strategy: 'memory',
    async abortAll() {
      await Promise.all(sinks.map((sink) => sink.abort()));
    },
  };
}
